import styled, { css } from 'styled-components';
import { Image } from '../../../commonTypes/image';
import colors, { colorWithOpacity } from '../../../helpers/colors';
import fonts from '../../../helpers/fonts';
import mediaQuery from '../../../helpers/mediaQuery';
import spacing from '../../../helpers/spacing';
import { PrimaryCta, SecondaryCta } from '../../Cta';
import RichText from '../../RichText';

export const ReversibleDiv = styled.div`
  display: flex;
  flex-flow: column-reverse;

  // Non-standard breakpoint of 1000px requested by designer
  @media (min-width: 1000px) {
    flex-flow: row;
    padding-bottom: 0;
  }
`;

export const TextContainer = styled.div<{ hasMedia?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: ${({ hasMedia }) => (hasMedia ? spacing(5) : spacing(8))};
  padding-bottom: ${({ hasMedia }) => (hasMedia ? spacing(6) : spacing(8))};
  flex-shrink: 1;
  width: 100%;
  @media (min-width: 1000px) {
    width: ${({ hasMedia }) => (hasMedia ? '50%' : '736px')};
    min-width: 50%;
    padding-top: ${spacing(8)};
    padding-bottom: ${spacing(8)};
    padding-right: ${({ hasMedia }) => (hasMedia ? spacing(9.25) : '0')};
  }
`;

export const TextContent = styled.div<{ hasCtas?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: ${({ hasCtas }) => (hasCtas ? 'fit-content' : '100%')};
`;

export const Heading = styled.h1`
  ${fonts.headingLarge}
  margin: 0;

  & {
    color: ${colors.core01};
    font-weight: 500;
    font-size: 52px;
    line-height: 60px;
    ${mediaQuery.tabletPortrait`
      ${fonts.headingXLarge}
    `}
  }
`;

export const Subhead = styled.p`
  ${fonts.headingSmall}
  margin: ${spacing(2)} 0 0;

  & {
    color: ${colors.neutral01};
    font-weight: 500;
  }
`;

export const Body = styled(RichText)`
  ${fonts.paragraph}
  margin: ${spacing(2)} 0 0;

  & {
    color: ${colors.neutral01};
  }
`;

type CtasContainerProps = {
  noOfSections: number;
};

export const CtasContainer = styled.div<CtasContainerProps>`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-direction: row;
  flex-wrap: wrap;

  margin-top: ${spacing(1)};
  margin-left: ${spacing(-3)};
  margin-bottom: ${spacing(2)};

  ${mediaQuery.tabletLandscape`
    margin-left: ${spacing(-4)};
    margin-bottom: 0;
  `}

  text-align: left;

  ${mediaQuery.tabletPortrait`
    margin-top: ${spacing(2)};
  `}

  ${mediaQuery.tabletLandscape`
    ${(props) =>
      props.noOfSections > 2 &&
      css`
        justify-content: center;
      `}

    ${(props) =>
      props.noOfSections === 3 &&
      css`
        margin-top: ${spacing(4)};
      `}
  `}
`;

const StyledCta = css`
  flex: 0 1 auto;
  
  margin-left: ${spacing(3)};
  
  ${mediaQuery.tabletLandscape`
    margin-left: ${spacing(4)};
  `}

  margin-top: ${spacing(2)};
  min-width: unset;
`;

export const StyledSecondaryCta = styled(SecondaryCta)`
  ${StyledCta};
`;

export const StyledPrimaryCta = styled(PrimaryCta)`
  ${StyledCta};
`;

export const MediaContainer = styled.div`
  width: 100vw;
  height: 100vw;

  // Top margin set to address spacing issue created by header + nav
  margin: ${spacing(0.5)} calc(50% - 50vw) 0;

  // Non-standard breakpoint of 480px requested by designer
  @media (min-width: 480px) {
    height: 480px;
  }

  ${mediaQuery.tabletPortrait`
    margin-top: ${spacing(1.5)};
  `}

  ${mediaQuery.tabletLandscape`
    margin-top: 0;
  `}

  // Non-standard breakpoint of 1000px requested by designer
  //
  // min-height uses calc to match the designer's request with a linear function:
  // - screen width 1440px => min-height: 456px
  // - screen width 2560px => min-height: 566px
  @media (min-width: 1000px) {
    width: 50vw;
    height: auto;
    min-height: calc(315px + 9.82vw);
    margin: 0 calc(50% - 50vw) 0 0;
  }
`;

export const MediaWrapper = styled.figure`
  height: 100%;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  margin-top: 0;
  margin-bottom: 0;

  ${mediaQuery.tabletLandscape`
    padding: 0;
  `}
`;

export const MainImage = styled.div<{ image: Image }>`
  height: 100%;
  background-image: url(${(props) => props.image?.url || ''});
  background-size: cover;
  background-position: center center;
`;

export const BottomSpacing = styled.div<{
  spaceHeight: number;
  addBackgroundColour: boolean;
}>`
  height: ${(props) => spacing(props.spaceHeight)};
  background-color: ${(props) =>
    props.addBackgroundColour ? colorWithOpacity(colors.core01, 0.1) : colors.neutral08};
  margin: 0 calc(50% - 50vw);
`;
